import { useEffect, useState } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const VerifyCommissionNFSADetailsReport = ({
  month,
  year,
  allotMonth,
  allotYear,
  distMonthName,
  alotMonthName,
  distCode,
  userRole,
}) => {
  const [commissionData, setCommissionData] = useState([]);
  const [verifyNfsaAbsCommission, setVerifyNfsaAbsCommission] = useState([]);
  const [submitMessage, setSubmitMessage] = useState("");
  const [loading, setLoading] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetCommissionCost = async () => {
      try {
        setLoading(true);
        const commissioncostresponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/commission-cost`
        );

        if (!commissioncostresponse.ok) {
          throw new Error("Failed to fetch State commission data");
        }

        const data = await commissioncostresponse.json();
        setCommissionData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetCommissionCost();
  }, []);

  useEffect(() => {
    const fetchVerifyNFSACommissionDetails = async () => {
      try {
        setSubmitMessage("");
        setLoading(true);
        const verifyCommissionResponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/verify-nfsa-commission?month=${month}&year=${year}&alotMonth=${allotMonth}&alotYear=${allotYear}&distCode=${distCode}`
        );
        if (!verifyCommissionResponse.ok) {
          throw new Error("Failed to fetch nfsa commission abstract");
        }
        const data = await verifyCommissionResponse.json();
        setVerifyNfsaAbsCommission(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchVerifyNFSACommissionDetails();
  }, [month, year, allotMonth, allotYear, distCode]);

  const totals = verifyNfsaAbsCommission.reduce(
    (acc, item) => {
      acc.shopTotalQty += Number(item.shopTotalQty) || 0;
      acc.nfsaAmount += Number(item.nfsaAmount) || 0;
      acc.impdsTransCount += Number(item.impdsTransCount) || 0;
      acc.impdsTotalQty += Number(item.impdsTotalQty) || 0;
      acc.amountRecovered += Number(item.amountRecovered) || 0;
      acc.netPayable += Number(item.netPayable) || 0;
      acc.count += 1;
      return acc;
    },
    {
      shopTotalQty: 0,
      nfsaAmount: 0,
      impdsTransCount: 0,
      impdsTotalQty: 0,
      amountRecovered: 0,
      netPayable: 0,
      count: 0,
    }
  );

  const handleDownloadExcel = async (data) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Verify NFSA Commission ");

  const totalColumns = 8;

  // Add the report title row with colspan (merge cells)
  worksheet.mergeCells(1, 1, 1, totalColumns); 
  const titleCell = worksheet.getCell("A1");
  titleCell.value =
    "Monthly FPS Dealer Commission Report (SNA-SPARSH NFSA Format)";
  titleCell.font = { size: 12, bold: true };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };

  // Define header row
  const headers = [
    "S.No",
    "District",
    "NFSA Qty of Rice Distributed (A)",
    `Amount Payable Towards Commission  (In Rs ${commissionData.nfsaCost || 0} per kg)  ( B = A * ${commissionData.nfsaCost || 0})`,
    `Total No of IMPDS  ${distMonthName} - ${year}`,
    `Total Qty Distributed(Kgs) under NFSA  ${distMonthName} - ${year}`,
    "Amount Recovered (C)",
    "Net Amount Payable (D = B-C)"
  ];
  worksheet.addRow(headers);

  // Add data rows starting from row 3
  verifyNfsaAbsCommission.forEach((item, index) => {
    const row = worksheet.addRow([
      index + 1,
      item.districtName,
      item.shopTotalQty,      // keep as number
      item.nfsaAmount,
      item.impdsTransCount,
      item.impdsTotalQty,
      item.amountRecovered,
      item.netPayable
    ]);

    // Alignment
    for (let i = 1; i <= 2; i++) {
      row.getCell(i).alignment = { horizontal: "left" };
    }
    for (let i = 3; i <= 8; i++) {
      row.getCell(i).alignment = { horizontal: "right" };
    }

    // Number formatting
    row.getCell(3).numFmt = '0.000'; // Qty with 3 decimals
    row.getCell(4).numFmt = '0.00';  // Amount with 2 decimals
    row.getCell(6).numFmt = '0.000'; // Qty with 3 decimals
    row.getCell(7).numFmt = '0.00';  // Amount with 2 decimals
    row.getCell(8).numFmt = '0.00';  // Amount with 2 decimals
  });

  // Style header row
  const headerRow = worksheet.getRow(2);
  headerRow.font = { bold: true };
  headerRow.alignment = { horizontal: "center" };
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFDDDDDD" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Calculate totals
  const totals = verifyNfsaAbsCommission.reduce(
    (acc, item) => {
      acc.shopTotalQty += Number(item.shopTotalQty) || 0;
      acc.nfsaAmount += Number(item.nfsaAmount) || 0;
      acc.impdsTransCount += Number(item.impdsTransCount) || 0;
      acc.impdsTotalQty += Number(item.impdsTotalQty) || 0;
      acc.amountRecovered += Number(item.amountRecovered) || 0;
      acc.netPayable += Number(item.netPayable) || 0;
      return acc;
    },
    {
      shopTotalQty: 0,
      nfsaAmount: 0,
      impdsTransCount: 0,
      impdsTotalQty: 0,
      amountRecovered: 0,
      netPayable: 0,
    }
  );

  const totalRowNumber = worksheet.lastRow.number + 1;
  worksheet.mergeCells(totalRowNumber, 1, totalRowNumber, 2);

  const totalRow = worksheet.addRow([
    "",
    "Total",
    totals.shopTotalQty,
    totals.nfsaAmount,
    totals.impdsTransCount,
    totals.impdsTotalQty,
    totals.amountRecovered,
    totals.netPayable
  ]);

  // Style total row
  totalRow.font = { bold: true };
  totalRow.alignment = { horizontal: "right" };
  totalRow.getCell(1).alignment = { horizontal: "left" };

  totalRow.eachCell((cell, colNumber) => {
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "double" },
      right: { style: "thin" },
    };
    // Set number formatting for totals
    if (colNumber === 3 || colNumber === 6) cell.numFmt = '0.000';
    if (colNumber === 4 || colNumber === 7 || colNumber === 8) cell.numFmt = '0.00';
  });

  // Auto width adjustment
  worksheet.columns.forEach((col, index) => {
    let maxLength = 5;
    const headerText = headers[index] || "";
    if (headerText.length > maxLength) maxLength = headerText.length;

    col.eachCell({ includeEmpty: true }, (cell) => {
      let cellValue = cell.value;
      if (cellValue && typeof cellValue === "object") {
        if (cellValue.richText) cellValue = cellValue.richText.map(rt => rt.text).join("");
        else if (cellValue.text) cellValue = cellValue.text;
        else if (cellValue.result) cellValue = cellValue.result.toString();
        else cellValue = cellValue.toString();
      } else if (cellValue === null || cellValue === undefined) {
        cellValue = "";
      } else {
        cellValue = cellValue.toString();
      }
      if (cellValue.length > maxLength) maxLength = cellValue.length;
    });
    col.width = maxLength > 30 ? 30 : maxLength + 2;
  });

  // Save workbook
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  saveAs(blob, `Verify_NFSA_Wise_Commission.xlsx`);
};


  return (
    <div style={{ justifyContent: "center", marginBottom: "70px" }}>
      <table width="90%" border="1" cellPadding="5" cellSpacing="0">
        <thead>
          <tr className="tableheader">
            <th colSpan={13}>
              Monthly FPS Dealer Commission Report (SNA-SPARSH NFSA Format)
            </th>
          </tr>
          <tr className="tableheader">
            <th>S.No</th>
            <th>District</th>
            <th>
              NFSA <br /> Qty of Rice Distributed{" "}
              <font style={{ color: "orange" }}>(A)</font>
            </th>
            <th>
              Amount Payable Towards Commission <br /> (In Rs{" "}
              {commissionData.nfsaCost || 0} per kg) <br />{" "}
              <font style={{ color: "orange" }}>
                (B=A*{commissionData.nfsaCost || 0})
              </font>
            </th>
            <th>
              Total No of IMPDS <br /> {distMonthName} - {year}
            </th>
            <th>
              Total Qty Distributed(Kgs) under NFSA <br /> {distMonthName} -{" "}
              {year}
            </th>
            <th>
              Amount Recovered <font style={{ color: "orange" }}>(C)</font>
            </th>
            <th>
              Net Amount Payable <br />
              <font style={{ color: "orange" }}>(D = B-C)</font>
            </th>
          </tr>
        </thead>
        <tbody>
          {verifyNfsaAbsCommission.map((item, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{item.districtName}</td>
              <td style={{ textAlign: "right" }}>
                {item.shopTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {" "}
                {item.nfsaAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>{item.impdsTransCount}</td>
              <td style={{ textAlign: "right" }}>
                {item.impdsTotalQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.amountRecovered.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>
                {item.netPayable.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ fontWeight: "bold" }} className="tableheader">
            <td colSpan={2} style={{ textAlign: "right" }}>
              Total
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.shopTotalQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.nfsaAmount.toFixed(2)}
            </td>
            <td style={{ textAlign: "right" }}>{totals.impdsTransCount}</td>
            <td style={{ textAlign: "right" }}>
              {totals.impdsTotalQty.toFixed(3)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.amountRecovered.toFixed(2)}
            </td>
            <td style={{ textAlign: "right" }}>
              {totals.netPayable.toFixed(2)}
            </td>
          </tr>
        </tfoot>
      </table>
      <div style={{ marginTop: "20px", textAlign: "center" }}>
        {
          <button
            onClick={() => handleDownloadExcel(verifyNfsaAbsCommission)}
            className="button button2"
          >
            Download Excel
          </button>
        }
      </div>
    </div>
  );
};

export default VerifyCommissionNFSADetailsReport;
